import { useState } from 'react';
import { ZprAlert } from '@zpr-stencil';
import { ExpandIcon, PlusIcon, RefreshIcon } from '../../../icons';
import { AssetSource } from '../types';
import { AlertTypes } from '@zappar/zpr-ui-components-stencil/dist/types/components/alerts/specs.js';
import { DownloadAssetButton, MoveToBinButton } from './AssetActionButtons';

export interface AssetDetailActionsProps {
  binned: boolean;
  canDelete: boolean;
  canRestore: boolean;
  havePermissionToDelete: boolean;
  activeSource: AssetSource;
  onMoveToBin: () => void;
  onAddToWorkspaceAssets: () => Promise<void> | void;
  /** Pass only when "Asset details" button should be visible (e.g. isToolMode && onEdit) */
  onEdit?: () => void;
  onRestore?: () => void;
  /** Pass to render a "Download asset" button. Resolves when the download has been triggered. */
  onDownload?: () => Promise<void> | void;
  showDeleteConfirmation?: boolean;
}

export const AssetDetailActions = ({
  binned,
  canDelete,
  canRestore,
  activeSource,
  onMoveToBin,
  onAddToWorkspaceAssets,
  onEdit,
  onRestore,
  onDownload,
  havePermissionToDelete,
  showDeleteConfirmation = true,
}: AssetDetailActionsProps) => {
  const [isAddingToWorkspace, setIsAddingToWorkspace] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const handleAddToWorkspaceAssets = async () => {
    setIsAddingToWorkspace(true);
    try {
      await onAddToWorkspaceAssets();
    } finally {
      setIsAddingToWorkspace(false);
    }
  };

  const handleMoveToBin = () => {
    if (showDeleteConfirmation) {
      setIsConfirmingDelete(true);
    } else {
      onMoveToBin();
    }
  }

  return (
    <div className="asset-detail-actions">
      {!binned && activeSource !== 'trash' && !isConfirmingDelete && (
        <>
          {activeSource !== 'workspace-library' && (
            <button
              type="button"
              aria-label="Add to workspace assets"
              onClick={handleAddToWorkspaceAssets}
              className={`btn btn-ghost p0 ${isAddingToWorkspace ? ' processing' : ''}`}
              disabled={isAddingToWorkspace}
            >
              <PlusIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
              {activeSource === 'user-library' ? 'Move to workspace' : 'Copy to workspace'}
            </button>
          )}

          {onDownload && <DownloadAssetButton onDownload={onDownload} />}

          {onEdit && (
            <button
              className="btn btn-ghost p0 "
              type="button"
              aria-label="Asset details"
              onClick={onEdit}
            >
              <ExpandIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
              Asset details
            </button>
          )}
          <MoveToBinButton
            onClick={handleMoveToBin}
            havePermissionToDelete={canDelete && havePermissionToDelete}
          />
        </>
      )}

      {binned && canRestore && activeSource === 'trash' && !isConfirmingDelete && (
        <button
          className="btn btn-ghost p0 "
          type="button"
          aria-label="Restore file"
          onClick={onRestore}
        >
          <RefreshIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
          Restore file
        </button>
      )}

      {isConfirmingDelete && showDeleteConfirmation && (
        <div className="w100">
          <ZprAlert type={"warning" as AlertTypes} className='alert-plain alert-warning' dismissible={false}>
            Permanently deleting this media file may affect your published projects where this file is in use.
          </ZprAlert>
          <div className='flex btw center gap8 mt16'>
            <button className="btn btn-small btn-secondary" onClick={() => setIsConfirmingDelete(false)}>Cancel</button>
            <button className="btn btn-small btn-primary" onClick={onMoveToBin}>Delete</button>
          </div>
        </div>
      ) }
    </div>
  );
};
